/**
 * Détection des modifications non sauvegardées de l'éditeur de parcours.
 *
 * Une EMPREINTE des données enregistrables est prise au chargement du projet
 * et après chaque sauvegarde sans échec ; quitter l'éditeur alors que
 * l'empreinte courante diffère (ou qu'un tracé, une suppression ou un média
 * attend encore l'envoi) déclenche un avertissement.
 */

// Champs d'un POI réellement enregistrés (voir `markerData` dans
// `persistProjectData` ; tout champ ajouté là-bas doit l'être ici). Les
// champs dérivés ou techniques (icône de carte, onClick, serverId, lastLat,
// médias normalisés rafraîchis après envoi) n'en font pas partie, sinon le
// simple chargement passerait pour une modification.
const SAVED_MARKER_FIELDS = [
  "id",
  "lat",
  "lng",
  "type",
  "subType",
  "title",
  "description",
  "image",
  "iconImage",
  "iconName",
  "markerToDownload",
  "video",
  "audio",
  "checkAcces",
  "imageName",
  "videoName",
  "iconNameWithoutBorder",
  "audioName",
  "stopover",
  "mainResource",
  "mainImage",
  "resourceArray",
  "triggerType",
  "triggerDistance",
  "openQuestionArray",
  "qcmArray",
  "qcmImageArray",
  "url",
];

// `undefined`, `null` et "" sont équivalents : un champ absent au
// chargement peut être initialisé à vide par un formulaire sans modification.
const normalize = (value) =>
  value === undefined || value === null || value === "" ? null : value;

/**
 * Instantané comparable des données enregistrables du projet.
 * Les indications de navigation ne comptent que si l'utilisateur les a
 * éditées : sinon elles dérivent du tracé, suivi à part (`trackDirty`).
 *
 * Tout est figé en chaînes : les formulaires modifient les POI sur place,
 * un instantané qui garderait des références changerait avec eux. Les POI
 * restent indexés par `id` pour pouvoir retirer ou remplacer l'un d'eux
 * (suppression confirmée par le serveur, POI republié après envoi des
 * médias) sans recalculer le reste.
 */
export function projectSaveSnapshot({
  markers,
  originMarker,
  destinationMarker,
  customNavigationPoints,
  customIndicationsEdited,
}) {
  // `{}` (état initial) et absence de point sont équivalents.
  const position = (point) =>
    normalize(point?.lat) === null
      ? null
      : [normalize(point.lat), normalize(point.lng)];
  const edited = Boolean(customIndicationsEdited);
  return {
    markers: (markers || []).map(markerEntry),
    rest: JSON.stringify({
      origin: position(originMarker),
      destination: position(destinationMarker),
      customIndicationsEdited: edited,
      customNavigationPoints: edited ? customNavigationPoints || [] : null,
    }),
  };
}

const markerEntry = (marker) => ({
  id: marker?.id,
  fields: JSON.stringify(
    SAVED_MARKER_FIELDS.map((field) => normalize(marker?.[field]))
  ),
});

/** Remplace dans l'instantané les POI fournis (même `id`). */
export function withSnapshotMarkers(snapshot, markers) {
  const byId = new Map((markers || []).map((marker) => [marker.id, marker]));
  return {
    ...snapshot,
    markers: snapshot.markers.map((entry) =>
      byId.has(entry.id) ? markerEntry(byId.get(entry.id)) : entry
    ),
  };
}

/** Retire de l'instantané les POI dont l'`id` local est donné. */
export function withoutSnapshotMarkers(snapshot, markerIds) {
  const ids = new Set(markerIds);
  return {
    ...snapshot,
    markers: snapshot.markers.filter((entry) => !ids.has(entry.id)),
  };
}

/** Chaîne comparable d'un instantané (null si absent). */
export function snapshotSignature(snapshot) {
  return snapshot ? JSON.stringify(snapshot) : null;
}

/** Empreinte directe des données (instantané puis chaîne). */
export function projectSaveSignature(data) {
  return snapshotSignature(projectSaveSnapshot(data));
}

/**
 * Vrai si quitter l'éditeur ferait perdre quelque chose.
 * Sans empreinte de référence (chargement pas terminé), rien n'est signalé.
 */
export function hasUnsavedProjectChanges({
  baselineSignature,
  currentSignature,
  trackDirty = false,
  importedTracksChanged = false,
  pendingRemovals = 0,
  pendingMedia = false,
}) {
  if (baselineSignature === null || baselineSignature === undefined) {
    return false;
  }
  return (
    currentSignature !== baselineSignature ||
    Boolean(trackDirty) ||
    Boolean(importedTracksChanged) ||
    pendingRemovals > 0 ||
    Boolean(pendingMedia)
  );
}
